import { DebugLogger } from '@affine/debug';
import {
  type LocalAccountKekCustody,
  type LocalAccountKeyPort,
  type LocalAccountKeyRecord,
  LocalAccountKeyStore,
  type LocalAuthBinding,
} from '@affine/nbstore';
import { raiseNativeAccountMirrors } from '@affine/nbstore/sqlite';
import type {
  UnboundWorkerInitOptions,
  WorkerInitOptions,
} from '@affine/nbstore/worker/client';
import {
  LOCAL_DATA_ACCESS_ERROR_CODES,
  localAccountKeyError,
} from '@streamlain/view-config/local-data-seal';
import { Service } from '@toeverything/infra';

import type { Server } from '../cloud/entities/server';
import type { ServersService } from '../cloud/services/servers';

const logger = new DebugLogger('affine:local-data-keys');

export interface LocalAccountStoreContext {
  flavour: string;
  workspaceId: string;
  externalDocAccess?: { readonly identityId: string };
}

interface LoginCompletion {
  pending: Promise<LocalAccountKeyRecord>;
  failed: boolean;
}

/** Account state and transient login proofs; payload sealing/locking is W6. */
export class LocalAccountKeys extends Service implements LocalAccountKeyPort {
  private readonly stores = new Map<string, LocalAccountKeyStore>();
  private readonly accountServers = new Map<string, Server>();
  private readonly inflight = new Map<string, Promise<void>>();
  private readonly proofs = new Map<string, string>();
  private readonly completions = new Map<string, LoginCompletion>();

  constructor(
    private readonly servers: ServersService,
    private readonly kekCustody: LocalAccountKekCustody
  ) {
    super();
  }

  async resolveStoreOptions(
    options: UnboundWorkerInitOptions,
    context: LocalAccountStoreContext
  ): Promise<WorkerInitOptions> {
    const authBinding = await this.resolveBinding(context);
    const local: WorkerInitOptions['local'] = {};
    for (const key of [
      'doc',
      'blob',
      'docSync',
      'blobSync',
      'indexer',
      'indexerSync',
      'awareness',
    ] as const) {
      const storage = options.local[key];
      if (!storage) continue;
      switch (storage.name) {
        case 'IndexedDBDocStorage':
        case 'IndexedDBBlobStorage':
        case 'IndexedDBDocSyncStorage':
        case 'IndexedDBBlobSyncStorage':
        case 'IndexedDBIndexerStorage':
        case 'IndexedDBIndexerSyncStorage':
        case 'SqliteDocStorage':
        case 'SqliteBlobStorage':
        case 'SqliteDocSyncStorage':
        case 'SqliteBlobSyncStorage':
          local[key] = { ...storage, opts: { ...storage.opts, authBinding } };
          break;
        default:
          local[key] = storage;
      }
    }
    return { ...options, local };
  }

  async resolveBinding(
    context: LocalAccountStoreContext
  ): Promise<LocalAuthBinding | null> {
    let authBinding: LocalAuthBinding | null = null;
    if (context.externalDocAccess) {
      const identityId = context.externalDocAccess.identityId;
      if (!identityId) throw localAccountKeyError('LOCKED');
      const accountId = 'external:' + identityId;
      const store = this.getStore(accountId);
      // initialize uses CAS, so simultaneous guest opens share one persisted epoch.
      const record = (await store.read()) ?? (await store.initialize());
      authBinding = {
        accountId,
        workspaceId: context.workspaceId,
        authEpoch: record.authEpoch,
      };
      this.assertRecord(authBinding, record);
    } else if (context.flavour !== 'local') {
      const server = this.servers.server$(context.flavour).value;
      const accountId = server?.account$.value?.id;
      if (!server || !accountId) {
        logger.error('resolveBinding failed', {
          code: 'LOCKED',
          missing: server ? 'account' : 'server',
        });
        throw localAccountKeyError('LOCKED');
      }
      const completion = this.completions.get(
        this.completionKey(context.flavour, accountId)
      );
      if (completion) {
        try {
          if (completion.failed) throw localAccountKeyError('LOCKED');
          await completion.pending;
        } catch {
          logger.error('resolveBinding failed', { code: 'LOCKED' });
          throw localAccountKeyError('LOCKED');
        }
      }
      if (server.account$.value?.id !== accountId)
        throw localAccountKeyError('LOCKED');
      this.accountServers.set(accountId, server);
      const store = this.getStore(accountId);
      const record = (await store.read()) ?? (await store.initialize());
      authBinding = {
        accountId,
        workspaceId: context.workspaceId,
        authEpoch: record.authEpoch,
      };
      const key = this.epochKey(authBinding);
      let pending = this.inflight.get(key);
      if (!pending) {
        pending = this.resumeAccount(authBinding);
        this.inflight.set(key, pending);
        void pending.catch(error => {
          this.inflight.delete(key);
          const rawCode: unknown =
            error && typeof error === 'object' ? error.code : undefined;
          const code =
            LOCAL_DATA_ACCESS_ERROR_CODES.find(code => code === rawCode) ??
            'UNKNOWN';
          logger.error('resumeAccount failed', { code });
        });
      }
      // An account without any key on this device has no readable local data
      // yet, so its stores open only after the first key is installed. A
      // failed installation is reported by the first storage operation.
      if (!record.envelope && !record.keyId)
        await pending.catch(() => undefined);
    }
    return authBinding;
  }

  /** Called only with the proof from a completed normal login (never revalidation). */
  completeLogin(
    flavour: string,
    accountId: string,
    loginProof: string
  ): Promise<LocalAccountKeyRecord> {
    if (!loginProof || !accountId)
      return Promise.reject(localAccountKeyError('DENIED'));
    const key = this.completionKey(flavour, accountId);
    const pending = this.finishLogin(flavour, accountId, loginProof);
    const completion: LoginCompletion = { pending, failed: false };
    this.completions.set(key, completion);
    void pending.then(
      () => {
        if (this.completions.get(key) === completion)
          this.completions.delete(key);
      },
      () => {
        // Only memory retains the failure; the next normal login replaces it.
        completion.failed = true;
      }
    );
    return pending;
  }

  clearLoginCompletion(flavour: string, accountId: string): void {
    this.completions.delete(this.completionKey(flavour, accountId));
  }

  /** Locks the account durably; AuthService calls it before the sign-out request. */
  async beginLogout(flavour: string, accountId: string): Promise<void> {
    if (!accountId) throw localAccountKeyError('DENIED');
    this.clearLoginCompletion(flavour, accountId);
    for (const key of this.proofs.keys()) {
      if (JSON.parse(key)[0] === accountId) this.proofs.delete(key);
    }
    for (const key of this.inflight.keys()) {
      if (JSON.parse(key)[0] === accountId) this.inflight.delete(key);
    }
    const locked = await this.getStore(accountId).beginLogout();
    await this.raiseNativeMirrors(accountId, locked);
  }

  /**
   * The store raised the IndexedDB mirrors of the account; on Desktop the
   * native databases follow, so one of an older epoch refuses its next call.
   */
  private async raiseNativeMirrors(
    accountId: string,
    record: LocalAccountKeyRecord
  ) {
    if (this.kekCustody.kind !== 'os-vault') return;
    await raiseNativeAccountMirrors(accountId, {
      authEpoch: record.authEpoch,
      logoutPending: record.logoutPending,
    });
  }

  private async finishLogin(
    flavour: string,
    accountId: string,
    loginProof: string
  ): Promise<LocalAccountKeyRecord> {
    const server = this.servers.server$(flavour).value;
    // The opaque proof/account pair comes from the server before revalidation.
    if (!server) throw localAccountKeyError('LOCKED');
    this.accountServers.set(accountId, server);
    const store = this.getStore(accountId);
    const record = await store.read();
    const startEpoch = record?.authEpoch ?? 0;
    let completed: LocalAccountKeyRecord;
    try {
      completed = await store.completeLogin(startEpoch);
    } catch (error) {
      if (
        !error ||
        typeof error !== 'object' ||
        !('code' in error) ||
        error.code !== 'LOCKED'
      )
        throw error;
      // A neighbouring tab may have won this exact CAS. Re-read once only.
      const peer = await store.read();
      if (
        !peer ||
        peer.accountId !== accountId ||
        peer.logoutPending ||
        !Number.isSafeInteger(peer.authEpoch) ||
        peer.authEpoch <= startEpoch
      )
        throw error;
      completed = peer;
    }
    await this.raiseNativeMirrors(accountId, completed);
    for (const key of this.proofs.keys()) {
      if (JSON.parse(key)[0] === accountId) this.proofs.delete(key);
    }
    this.proofs.set(
      this.epochKey({ accountId, authEpoch: completed.authEpoch }),
      loginProof
    );
    return completed;
  }

  resumeAccount(binding: LocalAuthBinding): Promise<void> {
    const pending = this.inflight.get(this.epochKey(binding));
    if (pending) {
      return this.getStore(binding.accountId)
        .read()
        .then(record => {
          this.assertRecord(binding, record);
          return pending;
        });
    }
    return this.resume(binding);
  }

  private async resume(binding: LocalAuthBinding): Promise<void> {
    const store = this.getStore(binding.accountId);
    const record = this.assertRecord(binding, await store.read());
    const server = this.accountServers.get(binding.accountId);
    if (!server || server.account$.value?.id !== binding.accountId)
      throw localAccountKeyError('LOCKED');
    try {
      await store.readKek(binding);
      return;
    } catch (error) {
      if (
        !error ||
        typeof error !== 'object' ||
        !('code' in error) ||
        error.code !== 'KEY_UNAVAILABLE'
      )
        throw error;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false)
      throw localAccountKeyError('KEY_UNAVAILABLE');
    if (record.envelope || record.keyId) {
      if (!record.envelope || !record.keyId)
        throw localAccountKeyError('KEY_UNAVAILABLE');
      const key = this.epochKey(binding);
      const proof = this.proofs.get(key);
      if (!proof) throw localAccountKeyError('KEY_UNAVAILABLE');
      this.proofs.delete(key);
      const opened = await this.post(server, 'open', {
        accountId: binding.accountId,
        deviceId: record.deviceId,
        envelope: record.envelope,
        loginProof: proof,
      });
      const raw = this.decodeMaterial(opened.keyMaterial);
      try {
        this.assertCurrentAccount(server, binding);
        if (opened.keyId !== record.keyId)
          throw localAccountKeyError('KEY_UNAVAILABLE');
        await store.restoreKek(binding, { keyId: record.keyId, kek: raw });
      } finally {
        raw.fill(0);
      }
    } else {
      const raw = crypto.getRandomValues(new Uint8Array(32));
      try {
        const sealed = await this.post(server, 'envelope', {
          deviceId: record.deviceId,
          keyMaterial: btoa(String.fromCharCode(...raw)),
        });
        this.assertCurrentAccount(server, binding);
        if (
          typeof sealed.envelope !== 'string' ||
          !sealed.envelope ||
          typeof sealed.keyId !== 'string' ||
          !sealed.keyId
        )
          throw localAccountKeyError('KEY_UNAVAILABLE');
        await store.installKeys(binding, {
          envelope: sealed.envelope,
          keyId: sealed.keyId,
          kek: raw,
        });
      } finally {
        raw.fill(0);
      }
    }
    this.assertCurrentAccount(server, binding);
    await store.readKek(binding);
  }

  private getStore(accountId: string): LocalAccountKeyStore {
    let store = this.stores.get(accountId);
    if (!store) {
      store = new LocalAccountKeyStore({
        accountId,
        kekCustody: this.kekCustody,
      });
      this.stores.set(accountId, store);
    }
    return store;
  }

  private epochKey(binding: Pick<LocalAuthBinding, 'accountId' | 'authEpoch'>) {
    return JSON.stringify([binding.accountId, binding.authEpoch]);
  }

  private completionKey(flavour: string, accountId: string): string {
    return JSON.stringify([flavour, accountId]);
  }

  private assertRecord(
    binding: LocalAuthBinding,
    record: LocalAccountKeyRecord | null
  ): LocalAccountKeyRecord {
    if (
      !record ||
      record.accountId !== binding.accountId ||
      record.authEpoch !== binding.authEpoch ||
      record.logoutPending
    )
      throw localAccountKeyError('LOCKED');
    return record;
  }

  private assertCurrentAccount(server: Server, binding: LocalAuthBinding) {
    if (server.account$.value?.id !== binding.accountId)
      throw localAccountKeyError('LOCKED');
  }

  private async post(
    server: Server,
    operation: 'open' | 'envelope',
    body: object
  ): Promise<Record<string, unknown>> {
    try {
      const res = await server.fetch('/api/auth/local-data-key/' + operation, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw localAccountKeyError('KEY_UNAVAILABLE');
      const result: unknown = await res.json();
      if (!result || typeof result !== 'object' || Array.isArray(result))
        throw localAccountKeyError('KEY_UNAVAILABLE');
      return result as Record<string, unknown>;
    } catch {
      throw localAccountKeyError('KEY_UNAVAILABLE');
    }
  }

  private decodeMaterial(value: unknown): Uint8Array {
    if (typeof value !== 'string' || !/^[A-Za-z0-9+/]{43}=$/.test(value))
      throw localAccountKeyError('KEY_UNAVAILABLE');
    let raw: Uint8Array;
    try {
      raw = Uint8Array.from(atob(value), char => char.charCodeAt(0));
    } catch {
      throw localAccountKeyError('KEY_UNAVAILABLE');
    }
    if (raw.byteLength !== 32 || btoa(String.fromCharCode(...raw)) !== value) {
      raw.fill(0);
      throw localAccountKeyError('KEY_UNAVAILABLE');
    }
    return raw;
  }
}
